import { createFileRoute, useParams } from '@tanstack/react-router';
import { Alert, Card, Container, Loader, Skeleton, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconClock } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

/**
 * Public deletion-status page (M14 + M20).
 * Meta App-Review reviewers visit this URL to verify that the deletion
 * confirmation code we returned actually resolves to a real status row.
 */
function DeletionStatus() {
  const { code } = useParams({ from: '/deletion-status/$code' });
  const q = useQuery({
    queryKey: ['deletion-status', code],
    queryFn: async () => {
      const res = await fetch(`/api/meta/deletion-status/${code}`);
      if (!res.ok) throw new Error((await res.json()).error ?? 'not found');
      return (await res.json()) as {
        code: string;
        status: 'received' | 'in_progress' | 'completed' | 'failed';
        receivedAt: string;
        completedAt: string | null;
      };
    },
  });

  return (
    <Container size="sm" py="xl">
      <Stack gap="md">
        <Title order={2}>Deletion request status</Title>
        {q.isPending ? (
          <Skeleton height={140} />
        ) : q.isError ? (
          <Alert color="red" icon={<IconAlertCircle />}>
            {(q.error as Error).message}
          </Alert>
        ) : (
          <Card>
            <Stack gap="sm">
              <Text>
                Confirmation code:{' '}
                <Text component="span" ff="monospace">
                  {q.data.code}
                </Text>
              </Text>
              <StatusRow status={q.data.status} />
              <Text c="dimmed" size="sm">
                Received: {new Date(q.data.receivedAt).toLocaleString()}
              </Text>
              {q.data.completedAt && (
                <Text c="dimmed" size="sm">
                  Completed: {new Date(q.data.completedAt).toLocaleString()}
                </Text>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
}

function StatusRow({ status }: { status: 'received' | 'in_progress' | 'completed' | 'failed' }) {
  if (status === 'completed') {
    return (
      <Alert color="green" icon={<IconCheck />}>
        Your data has been deleted from LeadSync's systems.
      </Alert>
    );
  }
  if (status === 'failed') {
    return (
      <Alert color="red" icon={<IconAlertCircle />}>
        Deletion failed. Our team has been notified — please email
        <a href="mailto:privacy@leadsync.com"> privacy@leadsync.com</a> if you need a manual
        confirmation.
      </Alert>
    );
  }
  return (
    <Alert color="blue" icon={<IconClock />}>
      <Loader size="xs" mr="xs" /> Deletion in progress. Please check back in a few minutes.
    </Alert>
  );
}

export const Route = createFileRoute('/deletion-status/$code')({ component: DeletionStatus });
